/* Copyright (c) 2020-present CloudIO Inc */

import { IoPages, IoUsers } from '@cloudio-saas/datasource-types';
import {
  useStateX,
  useStateXAtomSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import {
  Box,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useCapturePubSub } from 'common/Context';
import { ExpressionProp, ExpressionValue } from 'componentregistry';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import DateInput from 'custom/DateInput';
import SelectInput from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import { expressionPropsAtom } from 'expression/builder/state';
import useLocalState from 'hooks/useLocalState';
import Icon from 'icons/Icon';
import { capitalize } from 'lodash-es';
import React, {
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import useAppStore from 'store/useAppStore';
import assert from 'utils/assert';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { ExpressionReturnType, PropertyEditorProps } from './types';
import { pagesFilter } from '../../PagesView/PagesPanel';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  tip: {
    marginLeft: 8,
    marginBottom: 8,
  },
  endAdornment: {
    right: 0,
  },
  inputRoot: {
    paddingRight: 0,
  },
});

const useOptionStyles = makeStyles((theme: Theme) => ({
  inner: {
    marginBottom: 8,
    display: 'flex',
    width: 88,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  option: {
    display: 'flex',
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    height: 32,
    outline: 'none',
    position: 'relative',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s',
    '& > div': {
      alignSelf: 'center',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    margin: '0 4px',
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
}));

function Option({
  icon,
  selected,
  onSelect,
  tip,
  testid,
  isViewOnlyUser = false,
}: {
  icon: React.ReactElement;
  selected?: boolean;
  onSelect: (event?: React.SyntheticEvent) => void;
  tip: string;
  testid: string;
  isViewOnlyUser: boolean;
}) {
  const classes = useOptionStyles();

  return (
    <span
      data-testid={testid}
      data-tip={tip}
      onKeyDown={(e) => clickOnEnterOrSpace(e, onSelect)}
      role="button"
      tabIndex={0}
      onMouseDown={!isViewOnlyUser ? onSelect : () => {}}
      onTouchStart={!isViewOnlyUser ? onSelect : () => {}}
      className={clsx(classes.option, selected ? classes.selected : null)}
      aria-label={tip}
    >
      {icon}
    </span>
  );
}

const BooleanSelectOptions = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' },
];

const YNSelectOptions = [
  { label: 'Checked', value: 'Y' },
  { label: 'Unchecked', value: 'N' },
];

const TFSelectOptions = [
  { label: 'Checked', value: 'T' },
  { label: 'Unchecked', value: 'F' },
];

function ExpressionProperty({
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  onChange,
  appUid,
  value: text2 = getDefaultValues<ExpressionValue>(mgr, property),
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<ExpressionProp, ExpressionValue>) {
  const text: ExpressionValue = useMemo(() => {
    const x: ExpressionValue =
      typeof text2 === 'string' ? { value: text2, type: 'text' } : text2;
    if (x.value === null) {
      return { type: 'text', value: '' };
    }
    return x;
  }, [text2]);
  const classes = useStyles();
  const [undoable, setUndoable] = useState(true);
  const optionClasses = useOptionStyles();
  assert(
    typeof text.value === 'string',
    `text.value must be string instead found ${typeof text.value} ${JSON.stringify(
      text,
    )}`,
  );
  const [localValue, setLocalValue] = useLocalState(text.value, (val) => {
    onChange(property, { type: text.type, value: val }, undoable);
    setUndoable(false);
  });
  const [state, setState] = useState([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const setExpressionProps = useStateXAtomSetter(expressionPropsAtom, {
    params: { appUid, pageId, itemId: activeItemId },
  });

  // do not use the designer path, instead use some transient path
  // as this could be used in UpdateProp Action in which case we need to
  // call onChange to change the property and not update the designer path directly
  const path = [
    'transient',
    '_expression_',
    appUid,
    pageId,
    activeItemId,
    property,
  ];
  const [expressionValue, setExpressionValue] = useStateX<
    ExpressionValue | undefined
  >(path, undefined);

  useEffect(() => {
    // reset the value in the trasient path
    setExpressionValue(undefined);
    return () => {
      setExpressionValue(undefined);
    };
  }, [setExpressionValue]);

  useEffect(() => {
    if (expressionValue) {
      // user have selected some item in the popup
      assert(
        typeof expressionValue.value === 'string',
        'expressionValue.value must be string',
      );
      setLocalValue(expressionValue.value);
      inputRef.current?.focus();
    }
  }, [expressionValue, setLocalValue]);

  useEffect(() => {
    if (def.refreshCallback) {
      def.refreshCallback(() => {
        setState([]);
      });
    }
  }, [def]);

  const [returnType, setReturnType] = useState<ExpressionReturnType>('string');

  useEffect(() => {
    const page = platform.getPage(appUid, pageId);
    const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
    def
      .returnType(itemWithDefaults, {
        appUid,
        itemId: activeItemId,
        pageId,
        mgr,
        platform,
      })
      .then((rt) => {
        setReturnType(rt);
      });
  }, [activeItemId, appUid, def, mgr, pageId, platform, state]);

  const getExprReturnType = (retType: ExpressionReturnType) => {
    if (retType === 'date') {
      return 'string';
    }
    if (retType === 'yn' || retType === 'tf') {
      return 'boolean';
    }
    return retType;
  };
  const openExpressionBuilder = (val: string) => {
    assert(typeof val === 'string', 'val must be a string');
    setExpressionProps({
      path,
      returnType: getExprReturnType(returnType),
      open: true,
      type: 'expressionObject',
      value: { type: 'expression', value: val },
      context: {
        appUid,
        pageId,
        itemId: activeItemId,
        property,
        data: {},
      },
    });
  };
  const inputType =
    text.type === 'expression' || returnType === 'string' ? 'text' : returnType;
  let textIcon = <Icon disabled={isViewOnlyUser} iconName="text" />;
  if (returnType === 'number') {
    textIcon = (
      <Icon
        disabled={isViewOnlyUser}
        iconName="io-number-input"
        iconType="cloudio"
      />
    );
  } else if (returnType === 'date') {
    textIcon = <Icon disabled={isViewOnlyUser} iconName="calendar" />;
  } else if (
    returnType === 'boolean' ||
    returnType === 'yn' ||
    returnType === 'tf'
  ) {
    textIcon = <Icon disabled={isViewOnlyUser} iconName="check-square" />;
  } else if (returnType === 'array') {
    textIcon = <Icon disabled={isViewOnlyUser} iconName="brackets" />;
  } else if (returnType === 'object') {
    textIcon = <Icon disabled={isViewOnlyUser} iconName="brackets-curly" />;
  } else if (returnType === 'user') {
    textIcon = <Icon disabled={isViewOnlyUser} iconName="users" />;
  } else if (returnType === 'page') {
    textIcon = <Icon disabled={isViewOnlyUser} iconName="browser" />;
  }

  const { store, query, isBusy } = useAppStore<IoUsers>({
    appUid,
    pageId: '$$Expression$$',
    ds: 'IoUsers',
    alias: 'IoUsers-alias',
  });

  const {
    store: pagesStore,
    query: pagesQuery,
    isBusy: pagesIsBusy,
  } = useAppStore<IoPages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesChooserAlias',
  });

  const users = useStoreRecords(store);

  const pages = useStoreRecords(pagesStore);

  useEffect(() => {
    if (inputType === 'user') {
      query({
        props: {
          limit: 5000,
          offset: 0,
          projection: {
            displayName: 1,
            emailAddress: 1,
            userName: 1,
          },
        },
      });
    } else if (inputType === 'page') {
      pagesQuery({ props: pagesFilter(appUid) });
    }
  }, [appUid, inputType, pagesQuery, query]);
  const id = useId();

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });
  const onAdd = def.onAdd;
  const onRemove = def.onRemove;
  const showTime = def?.hasTime;
  const pubSub = useCapturePubSub();

  const endAdornment = (
    <InputAdornment position="end">
      <div className={optionClasses.inner}>
        <Box
          sx={{
            width: 'calc(50% - 8px)',
            left: text.type === 'text' ? 0 : '50%',
          }}
          className={optionClasses.s}
        />
        <Option
          isViewOnlyUser={isViewOnlyUser}
          testid="text"
          icon={textIcon}
          onSelect={() => {
            let val = localValue;
            if (text.type !== 'text') {
              if (val && val.charAt(0) === '`') {
                val = val.substring(1, val.length - 1);
                setLocalValue(val);
              }
              onChange(property, { value: val, type: 'text' });
              setExpressionValue(undefined);
            }
          }}
          selected={text.type === 'text'}
          tip={capitalize(returnType === 'string' ? 'text' : returnType)}
        />
        <Option
          testid="expression"
          isViewOnlyUser={isViewOnlyUser}
          icon={<Icon disabled={isViewOnlyUser} iconName="sigma" />}
          onSelect={(event) => {
            pubSub.pub('click', { event: event as React.MouseEvent });
            let val = localValue;
            if (text.type !== 'expression') {
              if (
                val &&
                val.charAt(0) !== '`' &&
                ['string', 'date', 'page', 'user'].includes(returnType)
              ) {
                val = `\`${val}\``;
                setLocalValue(val);
              }
              onChange(property, {
                value: val,
                type: 'expression',
              });
              setExpressionValue(undefined);
            }
            openExpressionBuilder(val);
          }}
          selected={text.type === 'expression'}
          tip={
            text.type === 'expression'
              ? 'Open Expression Builder'
              : 'Expression'
          }
        />
      </div>
      {onAdd ? (
        <Icon
          disabled={isViewOnlyUser}
          onClick={onAdd}
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip="Add"
          iconName="plus"
        />
      ) : null}
      {onRemove ? (
        <Icon
          disabled={isViewOnlyUser}
          onClick={onRemove}
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip="Remove"
          iconName="times"
        />
      ) : null}
      {helpText ? (
        <Icon
          disabled={isViewOnlyUser}
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </InputAdornment>
  );

  const getInput = () => {
    if (inputType === 'date') {
      return (
        <DateInput
          id={id}
          required={required}
          data-testid={`${property}-date-input`}
          value={text.value ?? new Date().toISOString()}
          onChange={(val) => {
            onChange(property, { ...text, value: val }, undoable);
            setUndoable(false);
            setExpressionValue(undefined);
          }}
          label="Value"
          variant="standard"
          endAdornment={endAdornment}
          showTime={showTime}
        />
      );
    }
    if (inputType === 'boolean') {
      return (
        <SelectInput
          id={id}
          required={required}
          data-testid={`${property}-bool-input`}
          fullWidth
          value={text.value}
          onChange={(val) => {
            onChange(property, { ...text, value: val }, undoable);
            setUndoable(false);
            setExpressionValue(undefined);
          }}
          label="Value"
          variant="standard"
          options={BooleanSelectOptions}
          endIcon={endAdornment}
          autoCompleteClasses={{
            endAdornment: classes.endAdornment,
          }}
        />
      );
    }
    if (inputType === 'yn') {
      return (
        <SelectInput
          id={id}
          data-testid={`${property}-yn-input`}
          fullWidth
          value={text.value}
          onChange={(val) => {
            onChange(property, { ...text, value: val }, undoable);
            setUndoable(false);
            setExpressionValue(undefined);
          }}
          label="Value"
          variant="standard"
          options={YNSelectOptions}
          endIcon={endAdornment}
          autoCompleteClasses={{
            endAdornment: classes.endAdornment,
          }}
        />
      );
    }
    if (inputType === 'tf') {
      return (
        <SelectInput
          id={id}
          data-testid={`${property}-tf-input`}
          fullWidth
          value={text.value}
          onChange={(val) => {
            onChange(property, { ...text, value: val }, undoable);
            setUndoable(false);
            setExpressionValue(undefined);
          }}
          label="Value"
          variant="standard"
          options={TFSelectOptions}
          endIcon={endAdornment}
          autoCompleteClasses={{
            endAdornment: classes.endAdornment,
          }}
        />
      );
    }
    if (inputType === 'user') {
      return (
        <SelectInput
          id={id}
          required={required}
          data-testid={`${property}-user-input`}
          fullWidth
          value={text.value}
          onChange={(val) => {
            onChange(property, { ...text, value: val }, undoable);
            setUndoable(false);
            setExpressionValue(undefined);
          }}
          label="Value"
          variant="standard"
          options={users}
          endIcon={endAdornment}
          autoCompleteClasses={{
            endAdornment: classes.endAdornment,
          }}
          startIcon={isBusy ? <WaveDots /> : null}
          disabled={isBusy}
          placeholder={isBusy ? 'Loading ...' : 'Choose a user'}
          valueAttribute="userName"
          labelAttribute="displayName"
        />
      );
    }
    if (inputType === 'page') {
      let label = 'Page';
      if (property === 'embeddedPageId') {
        label = 'Embedded Page ID';
      }
      return (
        <SelectInput
          id={id}
          required={required}
          data-testid={`${property}-page-input`}
          fullWidth
          value={text.value}
          onChange={(val: any) => {
            onChange(property, { ...text, value: val }, undoable);
            setUndoable(false);
            setExpressionValue(undefined);
          }}
          label={label}
          variant="standard"
          // Hide current page, it should not be displayed because same page cannot be embedded within it
          options={pages.filter((page) => page.pageUid !== pageId)}
          endIcon={endAdornment}
          autoCompleteClasses={{
            endAdornment: classes.endAdornment,
          }}
          startIcon={pagesIsBusy ? <WaveDots /> : null}
          disabled={pagesIsBusy}
          placeholder={pagesIsBusy ? 'Loading pages...' : 'Choose a page'}
          valueAttribute="pageUid"
          labelAttribute="pageName"
        />
      );
    }
    return (
      <>
        <InputLabel htmlFor={id} required={required}>
          {def.label}
        </InputLabel>
        <Input
          disabled={isViewOnlyUser}
          id={id}
          spellCheck={text.type === 'text'}
          autoCorrect={text.type === 'text' ? undefined : 'off'}
          fullWidth
          placeholder={def.placeholder}
          onFocus={(e) => {
            e.target.select();
            setUndoable(true);
          }}
          onBlur={() => {
            setUndoable(true);
          }}
          type={inputType}
          value={localValue ?? ''}
          onChange={(e) => {
            setLocalValue(e.target.value);
            if (text.type === 'text') {
              onChange(property, { ...text, value: e.target.value }, undoable);
              setUndoable(false);
            }
            setExpressionValue(undefined);
          }}
          inputRef={inputRef}
          endAdornment={endAdornment}
          inputProps={{
            'data-testid': `${property}-input`,
          }}
        />
      </>
    );
  };

  return (
    <>
      <FormControl
        disabled={isViewOnlyUser}
        className={classes.root}
        classes={{ root: classes.input }}
        fullWidth
        variant="standard"
        data-testid={property}
      >
        {getInput()}
      </FormControl>
    </>
  );
}

export default memo(ExpressionProperty);
